import React, { useRef, useState, useEffect } from 'react'
import { useStepLoriStyles } from './styles'
import { Container } from '../../../../components'
import {
    Paper,
    Typography,
    TextField,
    IconButton,
    Tooltip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    useTheme,
} from '@material-ui/core'
import format from 'date-fns/format'
import { useLoriState } from '../../../../contexts/LoriContext'
import { TypingIndicator } from '../../../../navigation/layouts/Lori/Channels/LoriChannelContent/TypingIndicator'
import { useLoriVoice } from '../../../../hooks/useLoriVoice'
import {
    MuiPickersUtilsProvider,
    KeyboardDatePicker,
} from '@material-ui/pickers'
import DateFnsUtils from '@date-io/date-fns'
import CalendarTodayIcon from '@material-ui/icons/CalendarToday'
import ApartmentIcon from '@material-ui/icons/Apartment'
import CloseIcon from '@material-ui/icons/Close'
import { UnitSelector } from './components/UnitSelector'
import { useFinderSelection } from '../../../../hooks/useFinderSelection'
import { Folder, OrderedUnit, ezScheduleDetails } from '../../../../models'
import { useLocation } from 'react-router-dom'
import MicIcon from '@material-ui/icons/Mic'
import StopIcon from '@material-ui/icons/Stop'
import { SchedulePhase } from './types'
import { LoriReview } from './components/LoriReview'
import SendIcon from '@material-ui/icons/Send'
import ReplayIcon from '@material-ui/icons/Replay'
import { SetTemplateUnitsParams } from '../../../../hooks/useEzSchedule/types'
import { StepLoriWelcome } from './components/StepLoriWelcome'
import { ExistingDataGate } from './components/ExistingDataGate'
import { LoriText } from '../../../../navigation/layouts/Lori/Icons/LoriText'
import { useTimezone } from '../../../../contexts/timezone/timezone'
import { DateTime } from 'luxon'

const DATE_SELECTOR_TRIGGERS = [
    'Turn Start Date:',
    'Turn End Date:',
    'Transfer Start Date:',
    'Transfer End Date:',
]

const END_DATE_TRIGGERS = ['Turn End Date:', 'Transfer End Date:']

const needsDateSelector = (text: string): boolean =>
    DATE_SELECTOR_TRIGGERS.some((trigger) => text.includes(trigger))

const isEndDateTrigger = (text: string): boolean =>
    END_DATE_TRIGGERS.some((trigger) => text.includes(trigger))

const needsUnitSelector = (text: string): boolean => text.includes('Units:')

interface InlineDateSelectorProps {
    defaultDate: string
    onDateSelected: (formatted: string) => void
    disabled: boolean
}

const InlineDateSelector = ({
    defaultDate,
    onDateSelected,
    disabled,
}: InlineDateSelectorProps) => {
    const classes = useStepLoriStyles()
    const { currentTimezone } = useTimezone()
    const [open, setOpen] = useState(false)
    const [selectedDate, setSelectedDate] = useState<Date | null>(null)

    const defaultDateStr = DateTime.fromISO(defaultDate, { zone: 'utc' })
        .setZone(currentTimezone)
        .toFormat('MM/dd/yyyy')

    const handleChange = (date: Date | null) => {
        setSelectedDate(date)
        if (date) {
            onDateSelected(format(date, 'MM/dd/yyyy'))
            setOpen(false)
            setSelectedDate(null)
        }
    }

    return (
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Button
                className={classes.inlineSelectorButton}
                onClick={() => setOpen(true)}
                disabled={disabled}
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<CalendarTodayIcon />}
            >
                Select Date
            </Button>
            <KeyboardDatePicker
                open={open}
                onClose={() => setOpen(false)}
                value={selectedDate ?? defaultDateStr}
                onChange={handleChange}
                format="MM/dd/yyyy"
                inputVariant="outlined"
                className={classes.hiddenDatePicker}
                TextFieldComponent={() => null}
            />
        </MuiPickersUtilsProvider>
    )
}

interface Props {
    infrastructure: Folder
    onPhaseChange?: (phase: SchedulePhase) => void
    phaseId?: number
    ezSchedule: ezScheduleDetails
    buildTurnProjectButton: boolean
    loriReviewButton: boolean
    retryPreviousMessageButton: boolean
    setTemplateUnits: (params: SetTemplateUnitsParams) => void
    ezScheduleFetching?: boolean
    onGoBack: () => void
    resetPhase: () => Promise<unknown>
}

export const StepLori = (props: Props) => {
    const classes = useStepLoriStyles()
    const theme = useTheme()
    const inputRef = useRef<HTMLInputElement>(null)
    const chatMessagesRef = useRef<HTMLDivElement>(null)
    const [unitSelectorOpen, setUnitSelectorOpen] = useState(false)
    const [orderedUnits, setOrderedUnits] = useState<OrderedUnit[]>([])
    const { finderSelection, setSelection } = useFinderSelection('schedule')
    const location = useLocation()
    const {
        handleSendMessage,
        connectionReady,
        activeConversation,
        thinking,
        setThinking,
    } = useLoriState()

    const [localInput, setLocalInput] = useState<string>('')

    const activeURL = location.pathname
    const { recording, startRecording, stopRecording } = useLoriVoice(activeURL)

    const filteredMessages =
        activeConversation?.chat_messages?.filter(
            (msg) =>
                msg.role !== 'system' &&
                (msg.role === 'user' ||
                    (msg.message_items?.[0]?.value?.trim() ?? '') !== ''),
        ) ?? []

    useEffect(() => {
        const container = chatMessagesRef.current
        if (!container) return

        const scrollToBottom = () => {
            requestAnimationFrame(() => {
                container.scrollTop = container.scrollHeight
            })
        }

        scrollToBottom()

        const observer = new MutationObserver(scrollToBottom)
        observer.observe(container, { childList: true, subtree: true })

        return () => observer.disconnect()
    }, [])

    const handleBuildTurnProject = () => {
        props.onPhaseChange?.('review')
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            if (localInput.trim() && connectionReady) {
                handleSendMessage(localInput, window.location.pathname)
                setLocalInput('')
            }
        }
    }

    const handleSendClick = () => {
        if (localInput.trim() && connectionReady) {
            handleSendMessage(localInput, window.location.pathname)
            setLocalInput('')
        }
    }

    // Need to add some logic to include a system message to indicate to the agent that this
    // message caused failure in the previous attempt
    const handleRetryPreviousMessage = () => {
        // Need to find last user message and send it again
        const lastUserMessage = activeConversation?.chat_messages?.filter(
            (msg) => msg.role === 'user',
        )?.[
            activeConversation?.chat_messages?.filter(
                (msg) => msg.role === 'user',
            )?.length - 1
        ]
        if (lastUserMessage) {
            handleSendMessage(
                lastUserMessage.message_items?.[0]?.value ?? '',
                window.location.pathname,
            )
        }
    }

    return (
        <ExistingDataGate
            ezSchedule={props.ezSchedule}
            phaseId={props.phaseId ?? -1}
            onReset={props.resetPhase}
            onExit={props.onGoBack}
        >
            <Container className={classes.root}>
                <Paper elevation={0} className={classes.chatWindow}>
                    {/* Header */}
                    <div className={classes.headerRow}>
                        <div className={classes.chatHeader}>
                            <span
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'baseline',
                                    justifyContent: 'center',
                                    whiteSpace: 'nowrap',
                                    gap: 6,
                                }}
                            >
                                <span>Step by Step Turn with</span>
                                <span
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        lineHeight: 0,
                                        transform: 'translateY(2px)',
                                    }}
                                >
                                    <LoriText width={86} height={30} />
                                </span>
                            </span>
                        </div>
                    </div>

                    {/* Message List */}
                    <div ref={chatMessagesRef} className={classes.chatMessages}>
                        {/* Welcome Message */}
                        <StepLoriWelcome />
                        {filteredMessages.length ? (
                            filteredMessages.map((msg, idx) => {
                                const text = msg.message_items?.[0]?.value ?? ''
                                const isBot = msg.role !== 'user'
                                const showDateSelector =
                                    isBot && needsDateSelector(text)
                                const showUnitSelector =
                                    isBot && needsUnitSelector(text)
                                const selectorEnabled =
                                    idx === filteredMessages.length - 1

                                return (
                                    <div
                                        key={idx}
                                        className={
                                            msg.role === 'user'
                                                ? classes.userMsg
                                                : classes.botMsg
                                        }
                                    >
                                        {text}
                                        {showDateSelector && (
                                            <InlineDateSelector
                                                defaultDate={
                                                    isEndDateTrigger(text)
                                                        ? props.ezSchedule
                                                              .end_date
                                                        : props.ezSchedule
                                                              .start_date
                                                }
                                                onDateSelected={(formatted) =>
                                                    handleSendMessage(
                                                        formatted,
                                                        window.location
                                                            .pathname,
                                                    )
                                                }
                                                disabled={!selectorEnabled}
                                            />
                                        )}
                                        {showUnitSelector && (
                                            <Button
                                                className={
                                                    classes.inlineSelectorButton
                                                }
                                                onClick={() =>
                                                    setUnitSelectorOpen(true)
                                                }
                                                disabled={!selectorEnabled}
                                                variant="outlined"
                                                color="primary"
                                                size="small"
                                                startIcon={<ApartmentIcon />}
                                            >
                                                Select Units
                                            </Button>
                                        )}
                                    </div>
                                )
                            })
                        ) : (
                            <div>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    align="center"
                                >
                                    One moment while we connect to Lori...
                                </Typography>
                            </div>
                        )}
                        {/* Thinking Indicator */}
                        {thinking && <TypingIndicator theme={theme} />}

                        {/* Lori Review */}

                        {props.loriReviewButton && (
                            <LoriReview
                                ezSchedule={props.ezSchedule}
                                onProceedToReview={handleBuildTurnProject}
                                phaseId={props.phaseId ?? -1}
                                isLoading={props.ezScheduleFetching}
                            />
                        )}
                    </div>

                    {/* Input/Footer - fixed at bottom */}
                    <div className={classes.footerContainer}>
                        {/* Text/Voice Input Row */}
                        <div className={classes.chatInputRow}>
                            <TextField
                                className={classes.input}
                                placeholder="Type your response here..."
                                variant="outlined"
                                size="small"
                                value={localInput}
                                onChange={(e) => setLocalInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                                inputRef={inputRef}
                                InputProps={{
                                    classes: {
                                        root: classes.inputRoot,
                                    },
                                }}
                                disabled={!connectionReady}
                            />
                            <div className={classes.inputButtons}>
                                {/* Send Button */}
                                {props.retryPreviousMessageButton ? (
                                    <Tooltip title="Retry Previous Message">
                                        <IconButton
                                            disabled={!connectionReady}
                                            color={
                                                connectionReady
                                                    ? 'primary'
                                                    : 'default'
                                            }
                                            onClick={handleRetryPreviousMessage}
                                        >
                                            <ReplayIcon
                                                htmlColor={
                                                    theme.palette.text.secondary
                                                }
                                            />
                                        </IconButton>
                                    </Tooltip>
                                ) : (
                                    <Tooltip title="Send Message (Enter)">
                                        <IconButton
                                            onClick={handleSendClick}
                                            disabled={!connectionReady}
                                            color={
                                                connectionReady
                                                    ? 'primary'
                                                    : 'default'
                                            }
                                        >
                                            <SendIcon
                                                htmlColor={
                                                    theme.palette.text.secondary
                                                }
                                            />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {/* Mic Button */}
                                <Tooltip title="Record Message">
                                    <IconButton
                                        onClick={
                                            recording
                                                ? () => {
                                                      stopRecording()
                                                      setThinking(true)
                                                  }
                                                : startRecording
                                        }
                                        color={
                                            recording ? 'secondary' : 'default'
                                        }
                                        disabled={!connectionReady}
                                    >
                                        {recording && (
                                            <>
                                                <span
                                                    className={classes.micPulse}
                                                />
                                                <StopIcon />
                                            </>
                                        )}
                                        {!recording && <MicIcon />}
                                    </IconButton>
                                </Tooltip>
                            </div>
                        </div>
                    </div>

                    {/* Unit Selector Dialog */}
                    <Dialog
                        open={unitSelectorOpen}
                        onClose={() => setUnitSelectorOpen(false)}
                        maxWidth="md"
                        fullWidth
                    >
                        <DialogTitle className={classes.dialogTitle}>
                            Select Units
                            <IconButton
                                onClick={() => setUnitSelectorOpen(false)}
                            >
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent className={classes.dialogContent}>
                            <UnitSelector
                                infrastructure={props.infrastructure}
                                finderSelection={finderSelection}
                                onUnitSelectionChange={setSelection}
                                setTemplateUnits={props.setTemplateUnits}
                                setOrderedUnits={setOrderedUnits}
                                phaseId={props.phaseId}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={() => {
                                    setUnitSelectorOpen(false)
                                    console.log('orderedUnits', orderedUnits)
                                    console.log(
                                        'finderSelection',
                                        finderSelection,
                                    )
                                    // Send orderedUnits through websocket
                                    if (orderedUnits.length > 0) {
                                        handleSendMessage(
                                            JSON.stringify({
                                                orderedUnits,
                                            }),
                                            window.location.pathname,
                                            'user',
                                            true,
                                        )
                                    }
                                }}
                                color="primary"
                                variant="contained"
                            >
                                Done
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Paper>
            </Container>
        </ExistingDataGate>
    )
}
